import ApiService from './';
import { SMTPParams } from '../params/redux/integration';
/**
 * @heading1: Integration API service.
 * @paragraph: This module provides a set of methods to interact with the Integration API.
 * It handles operations like Fetching folder details and create folder.
 */
const IntegrationApi = {
    /**
     * @heading3: Fetch Folders
     * @paragraph: Sends a POST request to retrieve all folders available for the user.
     * @param: {string} token - The user's authentication token.
     * @param: {number} client_id - The parameters required id on which the folders will be fetch.
     * @returns: {Promise<unknown[]>} - The response containing a list of all folders.
     * @separator:
     */
    fetchIntegrations: (token: string) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'integrations',
            method: 'get',
            token
        });
    },
    fetchClientIntegration: (token: string, client_id: number) => {
        // Perform a Post request to remove stripe endpoint with the token
        return ApiService.apiHit({
            url: 'get-client-integrations/agency',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    /**
     * @heading3: Create Folder
     * @paragraph: Sends a POST request to Create a Folder.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @param: {string} folder_name - The folder_name containing the Name of folder for the operation.
     * @param: {number} client_id - The client_id containing location for the operation.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    removeIntegration: (token: string, type: 'calendar' | 'email' | 'meet' | 'twilio' | 'stripe', client_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'remove-integration',
            method: 'post',
            token,
            data: { type, client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Remove Stripe
     * @paragraph: Sends a POST request to remove Stripe account.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    removeStripeAccount: (token: string) => {
        // Perform a Post request to remove stripe endpoint with the token
        return ApiService.apiHit({
            url: 'remove-stripe-integration',
            method: 'post',
            token,
            data: {},
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Create Twilio Integration
     * @paragraph: Sends a POST request to add twilio account.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    createTwilioIntegration: (token: string, data: {
        account_sid: string;
        api_sid: string;
        auth_token: string;
        phone_number: string;
        client_id: number;
    }) => {
        // Perform a Post request to remove stripe endpoint with the token
        return ApiService.apiHit({
            url: 'create-twilio-integration',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Remove Twilio Integration
     * @paragraph: Sends a POST request to remove twilio account.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    removeTwilioIntegration: (token: string, client_id: number) => {
        // Perform a Post request to remove stripe endpoint with the token
        return ApiService.apiHit({
            url: 'remove-twilio-integration',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    connectSMTPIntegration: (token: string, data: SMTPParams) => {
        // Perform a Post request to remove stripe endpoint with the token
        return ApiService.apiHit({
            url: 'create-imap',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    fetchClientCustomerIntegration: (token: string, client_id: number, customer_id: number) => {
        // Perform a Post request to remove stripe endpoint with the token
        return ApiService.apiHit({
            url: 'get-client-customer-integrations',
            method: 'post',
            token,
            data: { client_id, customer_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    fetchFacebookIntegration: (token: string, client_id: number) => {
        // Perform a Post request to remove stripe endpoint with the token
        return ApiService.apiHit({
            url: 'get-facebook-integration',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    removeFacebookIntegration: (token: string, client_id: number) => {
        // Perform a Post request to remove stripe endpoint with the token
        return ApiService.apiHit({
            url: 'disconnect-page-integration',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    createApiKey: (token: string) => {
        // Perform a Post request to remove stripe endpoint with the token
        return ApiService.apiHit({
            url: 'generate-public-token',
            method: 'post',
            token,
            data: { },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    twilioIncomingCallLog: (token: string, to: string, from: string) => {
        // Perform a Post request to remove stripe endpoint with the token
        return ApiService.apiHit({
            url: 'twilio/incoming-call-log',
            method: 'post',
            token,
            data: { to, from },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    }
};

export default IntegrationApi;
